<template>
  <div>
    <h5 class="card-title">
      <i class="fas fa-pause-circle"></i>
      Denúncias Paradas
    </h5>

    <div v-if="loading" class="text-center">
      <b-spinner small></b-spinner>
      <span class="ml-2">Carregando...</span>
    </div>
    <div v-else-if="allComplaints.length === 0" class="text-muted text-center">
      <i class="fas fa-thumbs-up"></i>
      Nenhuma denúncia parada (em processamento) encontrada
    </div>
    <div v-else>
      <div class="mb-3">
        <div>
          <b-badge variant="danger" class="mr-2">
            Total: {{ totalInactive }}
          </b-badge>
          <small class="text-muted"
            >denúncias em processamento sem movimentação</small
          >
        </div>
      </div>

      <div class="complaints-list-container">
        <div class="complaints-header">
          <div class="row-content">
            <div class="protocol-section header-label">Protocolo</div>
            <div class="company-section header-label">Empresa</div>
            <div class="days-section header-label">Dias Parada</div>
            <div class="status-section header-label">Status</div>
          </div>
        </div>
        <div class="complaints-scroll" @scroll="handleScroll">
          <div class="complaints-list">
            <div
              v-for="complaint in visibleComplaints"
              :key="complaint._id"
              class="complaint-row"
              @click="viewComplaint(complaint)"
            >
              <div class="row-content">
                <div class="protocol-section">
                  <strong class="protocol-link">{{
                    complaint.protocol
                  }}</strong>
                </div>

                <div class="company-section">
                  <span class="company-name">{{ complaint.companyName }}</span>
                </div>

                <div class="days-section">
                  <b-badge variant="danger" class="days-badge">
                    <i class="fas fa-pause-circle mr-1"></i>
                    {{ Math.floor(complaint.daysSinceUpdate) }} dias
                  </b-badge>
                </div>

                <div class="status-section">
                  <b-badge :variant="getStatusVariant(complaint.status)">
                    {{ getStatusText(complaint.status) }}
                  </b-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getInactiveComplaints } from "../../../../api/charts";

export default {
  name: "InactiveComplaints",
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      data: [],
      loading: true,
      limit: 15,
    };
  },
  computed: {
    totalInactive() {
      return this.allComplaints.length;
    },
    allComplaints() {
      const complaints = [];
      this.data.forEach((company) => {
        company.complaints.forEach((complaint) => {
          complaints.push({
            ...complaint,
            companyName:
              company._id?.fantasyName || company._id?.name || "Sem empresa",
          });
        });
      });
      return complaints.sort((a, b) => b.daysSinceUpdate - a.daysSinceUpdate);
    },
    visibleComplaints() {
      return this.allComplaints.slice(0, this.limit);
    },
  },
  watch: {
    companyId() {
      this.fetchData();
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.limit = 15;
        const response = await getInactiveComplaints(this.companyId);
        this.data = response.data;
      } catch (error) {
        console.error("Erro ao carregar denúncias paradas:", error);
        this.$bvToast.toast("Erro ao carregar denúncias paradas", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    viewComplaint(complaint) {
      this.$router.push(`/denuncias/${complaint._id}`);
    },
    handleScroll(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (this.limit < this.allComplaints.length) {
          this.limit += 15;
        }
      }
    },
    getStatusText(status) {
      const statusMap = {
        0: "Nova",
        1: "Processando",
        2: "Concluída",
      };
      return statusMap[status] || "Desconhecido";
    },
    getStatusVariant(status) {
      const variantMap = {
        0: "warning",
        1: "primary",
        2: "success",
      };
      return variantMap[status] || "secondary";
    },
  },
};
</script>

<style scoped>
.complaints-list-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.complaints-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.header-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
}

.complaints-scroll {
  max-height: 460px;
  overflow-y: auto;
}

.complaints-list {
  padding: 0;
}

.complaint-row {
  border-bottom: 1px solid #f1f3f4;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.complaint-row:hover {
  background-color: #f8f9fa;
}

.complaint-row:last-child {
  border-bottom: none;
}

.row-content {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1fr;
  align-items: center;
  gap: 1rem;
}

.protocol-section,
.company-section,
.days-section,
.status-section,
.actions-section {
  display: flex;
  align-items: center;
}

.actions-section {
  justify-content: center;
}

.protocol-link {
  color: #007bff;
  font-weight: 500;
}

.company-name {
  color: #6c757d;
}

.action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

.complaint-row:hover .action-icon {
  color: #007bff;
}

.card-title .fas {
  margin-right: 8px;
  color: #dc3545;
}

.days-badge {
  font-weight: 600;
}
</style>
